import { AppBar, IconButton, Link, Menu, MenuItem, Typography, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import GitHubIcon from '@mui/icons-material/GitHub';
import { Link as RRLink, useLocation } from 'react-router-dom';
import { Fragment, useState } from 'react';
import NavMenu from './NavMenu';
import { Box } from '@mui/system';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RuleFolderIcon from '@mui/icons-material/RuleFolder';
import StorageIcon from '@mui/icons-material/Storage';
import BuildIcon from '@mui/icons-material/Build';
import ExtensionIcon from '@mui/icons-material/Extension';

const pages = [
    { name: "Home", to: "/" },
    { name: "Demo", to: "/demo" },
    { name: "Explore", to: "/network" },
    { name: "About", to: "/about" },
];

const NavbarBox = styled((props) => (
    <AppBar
        elevation={0}
        color="transparent"
        {...props}
    />
))(({ theme }) => ({
    position: "absolute",
    top: 0,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: theme.spacing(2),
    height: "6.4rem",
    [theme.breakpoints.up("sm")]: {
        height: "7rem",
        padding: theme.spacing(2, 2.5),
    },
}));

const NavbarIconButton = styled((props) => (
    <IconButton color="inherit" {...props} />
))({
    marginLeft: "auto",
});

const NavbarTitle = styled((props) => (
    <Typography
        color="secondary"
        variant="h1"
        component={RRLink}
        to="/"
        {...props}
    />
))(({ theme }) => ({
    position: "relative",
    fontSize: "3rem",
    letterSpacing: -1,
    fontFamily: "'montserrat'",
    fontWeight: 800,
    textDecoration: "none",
    marginTop: -1,
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.up("md")]: {
        fontSize: "3.3rem",
    },
}));

const NavItemBox = styled(Box)(
    ({ theme }) => ({
        display: "flex",
        alignItems: "center",
        marginLeft: "auto",
        marginRight: `-${theme.spacing(2)}`,
    })
);

const NavMenuItemBox = styled(Box)(
    ({ theme }) => ({
        display: "flex",
        flexDirection: "column",
        paddingTop: "7rem",
        marginTop: "2vh",
    })
);

const NavItem = styled((props) => (
    <Typography component="div" {...props} />
))(({ theme }) => ({
    fontFamily: "montserrat",
    fontWeight: 700,
    fontSize: "1.5rem",
    margin: theme.spacing(0, 1.9),
    marginTop: 2,
    textDecoration: "none",
    color: "#000",
}));

const DownloadMenuItem = styled((props) => (
    <MenuItem component={Link} {...props} />
))(({ theme }) => ({
    fontFamily: "montserrat",
    fontWeight: 600,
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
    whiteSpace: "normal",
}));

const DownloadItems = [
    {
        name: "Ruleset",
        href: "https://raw.githubusercontent.com/ruh010/locedge-db/master/ruleset.json",
        icon: <RuleFolderIcon sx={{ marginLeft: 0.25, transform: "scale(0.8)" }} />,
        description: "Regexes for extracting GeoHints",
    },
    {
        name: "Dataset",
        href: "https://github.com/ruh010/locedge/tree/master/data/pops",
        icon: <StorageIcon sx={{ marginLeft: 0.25, transform: "scale(0.8)" }} />,
        description: "Mappings between each GeoHint and a physical location",
    },
    {
        name: "LocEdge tool",
        href: "https://www.npmjs.com/package/locedge",
        icon: <BuildIcon sx={{ marginLeft: 0.25, transform: "scale(0.8)" }} />,
        description: "A simple CDN debugging tool based on a  captured HAR file",
    },
    {
        name: "Browser Extensions",
        href: "#",
        icon: <ExtensionIcon sx={{ marginLeft: 0.25, transform: "scale(0.8)" }} />,
        description: "Coming Soon"
    },
];

const DownloadMenu = (props) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    return (
        <Fragment>
            <NavItem
                onClick={(event) => setAnchorEl(event.currentTarget)}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    ...(open && {
                        "& .MuiSvgIcon-root": {
                            transform: "scale(0.9) rotate(180deg)",
                        }
                    }),
                }}
            >
                Download <KeyboardArrowDownIcon sx={{ transform: "scale(0.9)", transition: "transform 0.2s", }} />
            </NavItem>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
            >
                {DownloadItems.map(({ name, href, icon, description }) => (
                    <DownloadMenuItem key={name} href={href}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            {name} {icon}
                        </Box>
                        <Typography variant="caption" component="div">
                            {description}
                        </Typography>
                    </DownloadMenuItem>
                ))}
            </Menu>
        </Fragment>
    );
};

export default function Navbar(props) {
    const [menuOpen, setMenuOpen] = useState(false);
    const lgbk = useMediaQuery("(min-width: 1072px)");
    const location = useLocation();

    return (
        <Fragment>
            <NavbarBox>
                <NavbarTitle>
                    loc<span style={{ color: "black" }}>edge</span>
                </NavbarTitle>
                {lgbk && (
                    <NavItemBox>
                        {pages.map(({ name, to }) => (
                            <NavItem component={RRLink} to={to} key={name} sx={{
                                position: "relative",
                                "&::after": {
                                    content: "''",
                                    position: "absolute",
                                    width: "100%",
                                    transform: "scaleX(0)",
                                    height: 2.5,
                                    bottom: -0.5,
                                    left: 0,
                                    backgroundColor: "purple",
                                    transformOrigin: "bottom right",
                                    transition: "transform 0.25s ease-out",
                                },
                                "&:hover": {
                                    "&::after": {
                                        transform: "scaleX(1)",
                                        transformOrigin: "bottom left",
                                    },
                                },
                                ...(to === location.pathname && {
                                    color: "#555",
                                }),
                            }}>
                                {name}
                            </NavItem>
                        ))}
                        <DownloadMenu />
                        {/* <NavbarIconButton
                            href="https://github.com/ruh010/locedge"
                            target="_blank" rel="noreferrer noopener"
                            sx={{ marginRight: 0.5 }}
                        >
                            <GitHubIcon />
                        </NavbarIconButton> */}
                        {/* <img width={36} style={{marginRight: 8}} src={`${process.env.PUBLIC_URL}/icons/android-chrome-512x512.png`} /> */}
                    </NavItemBox>
                )}
                {!lgbk && (
                    <NavbarIconButton onClick={() => setMenuOpen(true)}>
                        <MenuIcon />
                    </NavbarIconButton>
                )}
            </NavbarBox>
            <NavMenu open={menuOpen}>
                <NavbarBox>
                    <NavbarTitle onClick={() => setMenuOpen(false)}>
                        loc<span style={{ color: "black" }}>edge</span>
                    </NavbarTitle>
                    <NavbarIconButton onClick={() => setMenuOpen(false)}>
                        <CloseIcon />
                    </NavbarIconButton>
                </NavbarBox>
                <NavMenuItemBox>
                    {pages.map(({ name, to }) => (
                        <NavItem
                            component={RRLink}
                            to={to}
                            key={name}
                            onClick={() => setMenuOpen(false)}
                            sx={{
                                paddingX: 3,
                                paddingY: 2,
                                fontSize: "1.75rem",
                                borderRadius: "10rem",
                                margin: "0.5rem 2vw",
                                transition: "transform 0.25s",
                                "&:hover": {
                                    backgroundColor: "#eee",
                                    boxShadow: "0 5px purple",
                                    transform: "translateY(-5px)"
                                },
                                ...(to === location.pathname && {
                                    color: "#555",
                                }),
                            }}
                        >
                            {name}
                        </NavItem>
                    ))}
                    <NavItem sx={{
                        paddingX: 3,
                        paddingY: 2,
                        fontSize: "1.75rem",
                        margin: "0.5rem 2vw",
                    }}>
                        <Box sx={{
                            display: "flex",
                            marginBottom: 2,
                            alignItems: "center",
                        }}>
                            Download <KeyboardArrowDownIcon sx={{ transform: "scale(0.9)" }} />
                        </Box>
                        {DownloadItems.map(({ name, href, icon, description }) => (
                            <DownloadMenuItem key={name} href={href}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    {name} {icon}
                                </Box>
                                <Typography variant="caption" component="div">
                                    {description}
                                </Typography>
                            </DownloadMenuItem>
                        ))}
                    </NavItem>
                </NavMenuItemBox>
            </NavMenu>
        </Fragment>
    );
};